import { useTimeZone } from '@/auth/useTimeZone';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { OnDemandGLALink } from '@/components/session/on-demand-gla-link';
import { BaseEntityTableGroupKey, EntityTable, EntityTableGroupBy } from '@/components/table/entity-table';
import { Button } from '@/components/ui/button';
import { RightDrawer } from '@/components/ui/right-drawer';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { stringIfNull } from '@/lib/string-utils';
import {
  formatInstant,
  formatTimeRange,
  getNeedAssessmentLabelForMakeupDiagnostic,
  isMakeupDiagnosticLesson,
} from '@airreading/common';
import { CoachViewableSessionFeedback, studentYearOption, toSessionFeedbackCompoundId } from '@airreading/common/data';
import { createColumnHelper, getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ClockIcon, Maximize2, PencilIcon, WallpaperIcon, X } from 'lucide-react';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { SessionFeedbackStatus } from '../session/session-feedback-status';
import { CoachViewableSessionFeedbackPage, SaveAndCloseHandle } from './coach-viewable-session-feedback-page';
import { TimeZoneTruncated } from '@/components/time-zone-truncated';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators';

type EntityTableProps = ComponentProps<typeof EntityTable>;

interface CoachViewableFeedbacksTableProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  sessionFeedbacks: CoachViewableSessionFeedback[];
  filters?: EntityTableProps['filters'];
  emptyState?: EntityTableProps['emptyState'];
  tableHookProps?: Omit<TableOptions<CoachViewableSessionFeedback>, 'columns' | 'data' | 'getCoreRowModel'>;
  isLoading?: boolean;
  showPagination?: boolean;
  groupBy?: EntityTableGroupBy<CoachViewableSessionFeedback, GroupKey>;
}

export const CoachViewableFeedbacksTableColumnIds = {
  feedbackStatus: 'feedbackStatus',
  viewDetailsCoach: 'viewDetailsCoach',
  time: 'time',
  student: 'student',
  lessonName: 'lessonName',
  schoolDistrict: 'schoolDistrict',
  grade: 'grade',
  viewDrawer: 'viewDrawer',
} as const;

const columnHelper = createColumnHelper<CoachViewableSessionFeedback>();

const createCoachViewableFeedbacksColumns = (
  displayTimeZone: string,
  onSelectFeedback: (feedback: CoachViewableSessionFeedback) => void,
) => [
  columnHelper.accessor((row) => row, {
    id: CoachViewableFeedbacksTableColumnIds.viewDetailsCoach,
    header: '',
    cell: ({ getValue, row }) => {
      const feedback = getValue();
      const rowIndex = row.index;

      return (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onSelectFeedback(feedback)}
          data-testid={`feedback-row-button-edit-${rowIndex + 1}`}
        >
          <PencilIcon size={16} />
        </Button>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: CoachViewableFeedbacksTableColumnIds.feedbackStatus,
    header: 'Feedback status',
    cell: ({ getValue }) => {
      const feedback = getValue();
      const {
        session: { hoursCanceledBeforeStart },
      } = feedback;
      return (
        <SessionFeedbackStatus
          feedbacks={[feedback]}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lateSentSuffixLabel="(late)"
        />
      );
    },
  }),
  columnHelper.accessor('session', {
    id: CoachViewableFeedbacksTableColumnIds.time,
    header: () => (
      <span className="flex items-center whitespace-nowrap">
        Time (
        <TimeZoneTruncated timeZone={displayTimeZone} />)
      </span>
    ),
    cell: ({ getValue }) => {
      const session = getValue();
      return (
        <div>
          {formatInstant(session.startInstant, displayTimeZone).formattedDateWithoutYear},{' '}
          {formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: CoachViewableFeedbacksTableColumnIds.student,
    header: 'Student',
    cell: ({ getValue }) => {
      const feedback = getValue();
      const { student, session, onDemandGLA } = feedback;
      const name = student.coachFacingDisplayName ?? '';
      const label = isMakeupDiagnosticLesson(session.lessonName)
        ? getNeedAssessmentLabelForMakeupDiagnostic(student.learningPathId, student.startingLessonId)
        : null;
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="capitalize">{name}</span>
          <StudentNeedsIndicators
            specialEd={student.specialEd}
            dyslexic={student.dyslexic}
            ell={student.ell}
            mode="icons"
          />
          {label ? <span className="capitalize">{label}</span> : null}
          {onDemandGLA && <OnDemandGLALink onDemandGLA={onDemandGLA} grade={student.grade} />}
        </div>
      );
    },
  }),
  columnHelper.accessor('session.lessonName', {
    id: CoachViewableFeedbacksTableColumnIds.lessonName,
    header: 'Lesson',
    cell: ({ getValue }) => {
      const lesson = getValue();
      return <div>{lesson}</div>;
    },
  }),
  columnHelper.accessor('student.grade', {
    id: CoachViewableFeedbacksTableColumnIds.grade,
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      return <div>{studentYearOption(stringIfNull(grade))}</div>;
    },
  }),
  columnHelper.accessor('student', {
    id: CoachViewableFeedbacksTableColumnIds.schoolDistrict,
    header: 'District',
    cell: ({ getValue }) => {
      const student = getValue();

      return <div>{student.orgName || 'Unknown'}</div>;
    },
  }),
];

export const CoachViewableFeedbacksTable = <GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  sessionFeedbacks,
  filters,
  tableHookProps,
  emptyState,
  isLoading,
  showPagination = false,
  groupBy,
}: CoachViewableFeedbacksTableProps<GroupKey>) => {
  const { timeZone } = useTimeZone();
  const [selectedFeedback, setSelectedFeedback] = useState<CoachViewableSessionFeedback | null>(null);
  const [isFullPage, setIsFullPage] = useState(false);
  const saveAndCloseRef = useRef<SaveAndCloseHandle | null>(null);
  const showConfirmationRef = useRef<(() => void) | null>(null);
  const setShouldConfirmOnCloseRef = useRef<((value: boolean) => void) | null>(null);

  const handleSelectFeedback = useCallback((feedback: CoachViewableSessionFeedback) => {
    setSelectedFeedback(feedback);
    setIsFullPage(false);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    // Clean up localStorage when closing
    if (saveAndCloseRef.current?.cleanupOnClose) {
      saveAndCloseRef.current.cleanupOnClose();
    }
    setSelectedFeedback(null);
  }, []);

  const handleBeforeClose = useCallback(async () => {
    if (saveAndCloseRef.current) {
      const result = await saveAndCloseRef.current.saveAndClose();
      // If we need to show confirmation and can't close, show the Dialog immediately
      if (!result.canClose && result.showConfirmation && showConfirmationRef.current) {
        showConfirmationRef.current();
      }
      return result.canClose;
    }
    return true;
  }, []);

  const handleRequestClose = useCallback(() => {
    // Check if there are unsaved changes before closing
    if (saveAndCloseRef.current) {
      const hasUnsavedChanges = saveAndCloseRef.current.hasUnsavedChanges();
      if (hasUnsavedChanges) {
        // Set shouldConfirmOnClose to true and show confirmation dialog without saving
        if (setShouldConfirmOnCloseRef.current) {
          setShouldConfirmOnCloseRef.current(true);
        }
        if (showConfirmationRef.current) {
          showConfirmationRef.current();
        }
      } else {
        // No unsaved changes, close immediately
        handleCloseDrawer();
      }
    } else {
      // No ref available, close immediately
      handleCloseDrawer();
    }
  }, [handleCloseDrawer]);

  const columns = useMemo(
    () => createCoachViewableFeedbacksColumns(timeZone, handleSelectFeedback),
    [timeZone, handleSelectFeedback],
  );

  const table = useReactTable({
    data: sessionFeedbacks,
    columns,
    getRowId: (row) => toSessionFeedbackCompoundId(row),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
    ...tableHookProps,
  });

  return (
    <>
      <EntityTable
        table={table}
        filters={filters}
        emptyState={emptyState}
        isLoading={isLoading}
        showPagination={showPagination}
        cellTestId={(_, columnId, rowIndex) => {
          if (columnId === CoachViewableFeedbacksTableColumnIds.viewDetailsCoach) {
            return undefined;
          }
          const columnIdToField: Record<string, string> = {
            [CoachViewableFeedbacksTableColumnIds.feedbackStatus]: 'status',
            [CoachViewableFeedbacksTableColumnIds.time]: 'time',
            [CoachViewableFeedbacksTableColumnIds.student]: 'student',
            [CoachViewableFeedbacksTableColumnIds.lessonName]: 'lesson',
            [CoachViewableFeedbacksTableColumnIds.grade]: 'grade',
            [CoachViewableFeedbacksTableColumnIds.schoolDistrict]: 'school-district',
          };
          const field = columnIdToField[columnId] || columnId;
          return `feedback-row-cell-${field}-${rowIndex + 1}`;
        }}
        groupBy={groupBy}
      />
      {selectedFeedback && (
        <RightDrawer
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              handleCloseDrawer();
            }
          }}
          hideCloseButton={true}
          title={
            <div>
              {!isFullPage && (
                <div className="text-[30px] font-semibold mb-2 max-[900px]:text-left flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="shrink-0 capitalize">{selectedFeedback.student.coachFacingDisplayName}</span>
                    <StudentNeedsIndicators
                      specialEd={selectedFeedback.student.specialEd}
                      dyslexic={selectedFeedback.student.dyslexic}
                      ell={selectedFeedback.student.ell}
                      mode="full-text"
                    />
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsFullPage(!isFullPage)}
                      title={isFullPage ? 'Minimize' : 'Expand to full page'}
                      className="h-8 w-8 p-0"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={handleRequestClose}
                      title="Close"
                      className="h-8 w-8 p-0"
                      data-testid="feedbacks-table-close-button"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <ClockIcon size={16} className="mr-1 text-gray-400" />
                  <span>
                    {formatInstant(selectedFeedback.session.startInstant, timeZone).formattedDateWithoutYear},{' '}
                    {formatTimeRange(
                      selectedFeedback.session.startInstant,
                      selectedFeedback.session.endInstant,
                      timeZone,
                    )}
                  </span>
                </div>
                <div className="flex items-center">
                  <WallpaperIcon size={16} className="mr-1 text-gray-400" />
                  <span>{selectedFeedback.session.lessonName}</span>
                </div>
              </div>
            </div>
          }
          dismissible={false}
          onBeforeClose={handleBeforeClose}
          confirmationTitle="Unsaved changes"
          confirmationDescription="You have unsaved changes. Are you sure you want to close without saving?"
          confirmationConfirmText="Close without saving"
          confirmationCancelText="Stay"
          widthClass="bg-[#F7F7F9] w-[40%] min-w-[900px] max-w-[90%] max-[900px]:w-[100%] max-[900px]:min-w-[480px] max-[900px]:max-w-full max-[900px]:sm:max-w-full"
        >
          {({ closeDrawer, setShouldConfirmOnClose, showConfirmation }) => {
            showConfirmationRef.current = showConfirmation;
            setShouldConfirmOnCloseRef.current = setShouldConfirmOnClose;
            return (
              <InfoPageSuspenseBoundary
                onError={(e) => {
                  const axiosError = axiosErrorFrom(e);
                  const airServerError = airServerErrorFrom(e);

                  return {
                    allowRetry: axiosError?.response?.status !== 404,
                    errorHeader:
                      airServerError?.userFacingMessage ||
                      'Failed to load feedback form. Please try again or contact Air Reading support',
                  };
                }}
              >
                <CoachViewableSessionFeedbackPage
                  feedbackCompoundId={{
                    coachId: selectedFeedback.coach.id,
                    sessionId: selectedFeedback.session.id,
                    studentId: selectedFeedback.student.id,
                  }}
                  suppressStudentTitleRow
                  onClose={(bypassConfirmation) => {
                    if (bypassConfirmation) {
                      // When closing with bypass (confirmed), clean up and close
                      handleCloseDrawer();
                    } else {
                      // When closing without bypass, let the component handle confirmation
                      closeDrawer();
                      handleCloseDrawer();
                    }
                  }}
                  setShouldConfirmOnClose={setShouldConfirmOnClose}
                  isFullPage={isFullPage}
                  onFullPageChange={setIsFullPage}
                  saveAndCloseRef={saveAndCloseRef}
                  showConfirmation={showConfirmation}
                />
              </InfoPageSuspenseBoundary>
            );
          }}
        </RightDrawer>
      )}
    </>
  );
};
